'use client';

import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { AnchorProps } from '@headlessui/react/dist/internal/floating';
import { DoorOpen, LucideIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useClerk, useUser } from '@clerk/nextjs';
import { Fragment, useEffect } from 'react';
import Link from 'next/link';
import clsx from 'clsx';

import { Avatar, AvatarFallback, AvatarImage } from '@components/common/avatar';
import { Separator } from '@components/common/separator';
import { useCartStore } from '@lib/stores/useCartStore';
import { useUserStore } from '@lib/stores/useUserStore';
import { Skeleton } from '@components/common/skeleton';
import { abbreviateByEnds } from '@lib/utils/string';
import { UserService } from '@lib/user/services';
import { useClient } from '@lib/hooks/useClient';
import { capitalizedPerWord } from '@lib/utils';
import { Routes } from '@lib/routes';

import { FullLightLogo } from './logo';

interface ProfileButtonLinkProps {
  onClick?: () => void;
  href: string;
  label: string;
  icon: LucideIcon;
}

interface ProfileButtonProps {
  showName?: boolean;
  anchor?: AnchorProps;
  isDark?: boolean;
  onSignOut?: () => void;
  offset?: 'mt-1' | '-mt-1';
  links: ProfileButtonLinkProps[];
}

export function ProfileButton({
  isDark = false,
  ...props
}: Readonly<ProfileButtonProps>) {
  const { user, isLoaded, isSignedIn } = useUser();
  const isClient = useClient();

  if (!isLoaded || !isClient) {
    return <ProfileButtonSkeleton isDark={isDark} />;
  }

  if (!isSignedIn) return;

  return (
    <ProfileButtonContent
      user={{
        id: user.id,
        username: user.username!,
        imageUrl: user.imageUrl,
      }}
      isDark={isDark}
      {...props}
    />
  );
}

interface ProfileButtonContentProps extends ProfileButtonProps {
  user: {
    id: string;
    username: string;
    imageUrl: string;
  };
}
function ProfileButtonContent({
  user: clerkUser,
  showName = true,
  anchor,
  isDark = false,
  onSignOut,
  offset = 'mt-1',
  links,
}: Readonly<ProfileButtonContentProps>) {
  const { user, setUser } = useUserStore();
  const { signOut } = useClerk();
  const setCart = useCartStore((state) => state.setCart);
  const { data } = useQuery({
    queryFn: () => UserService.getUserById(clerkUser.id),
    queryKey: [clerkUser.id],
  });

  useEffect(() => {
    setUser(data);
  }, [data, setUser]);

  if (!user) {
    return <ProfileButtonSkeleton isDark={isDark} />;
  }

  return (
    <Popover className='relative flow-root max-[256px] lg:max-[512px] overflow-hidden'>
      {({ close }) => (
        <>
          <PopoverButton className='flex items-center gap-x-2.5 outline-none w-fit group'>
            <Avatar size='sm' className='relative overflow-hidden'>
              <AvatarImage
                src={clerkUser.imageUrl}
                alt={`${user.shopName || clerkUser.username}'s Profile`}
                sizes='(max-width: 768px) 80px, (max-width: 1200px) 120px, 160px'
              />
              <AvatarFallback>
                {user.shopName
                  ? abbreviateByEnds(user.shopName)
                  : abbreviateByEnds(clerkUser.username)}
              </AvatarFallback>
              <span className='absolute z-10 inset-0 -rotate-45 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-40 translate-x-[-100%] group-hover:translate-x-[100%] transition-all duration-200 ease-in-out pointer-events-none' />
            </Avatar>
            <span
              className={clsx(
                'text-sm font-medium truncate group-hover:opacity-80 transition-opacity duration-500 ease-in-out',
                isDark ? 'text-primary-foreground' : 'text-foreground',
                showName ? 'inline' : 'hidden',
              )}
            >
              {user.shopName || capitalizedPerWord(clerkUser.username)}
            </span>
          </PopoverButton>
          <Transition
            as={Fragment}
            enter='transition ease-out duration-100'
            enterFrom='opacity-0 translate-y-1'
            enterTo='opacity-100 translate-y-0'
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100 translate-y-0'
            leaveTo='opacity-0 translate-y-1'
          >
            <PopoverPanel
              anchor={anchor}
              className={clsx(
                'bg-gray-100 z-50 shadow-lg rounded-md ring-1 ring-primary/5 overflow-hidden p-px text-sm min-w-[312px] text-foreground/50',
                offset,
              )}
            >
              <div className='flex items-center gap-x-4 px-4 py-2.5 w-full bg-background'>
                <Avatar size='sm'>
                  <AvatarImage
                    src={clerkUser.imageUrl}
                    alt={`${user.shopName || clerkUser.username}'s Profile`}
                  />
                  <AvatarFallback>
                    {user.shopName
                      ? abbreviateByEnds(user.shopName)
                      : abbreviateByEnds(clerkUser.username ?? '')}
                  </AvatarFallback>
                </Avatar>
                <div className='flex flex-col truncate flex-1'>
                  <span className='text-sm font-medium text-black'>
                    {user.shopName || capitalizedPerWord(clerkUser.username)}
                  </span>
                  <span className='text-xs'>@{clerkUser.username}</span>
                </div>
              </div>

              <Separator />

              {links.map((link) => (
                <Link
                  key={link.label}
                  className='whitespace-nowrap flex items-center gap-x-2 px-4 py-2.5 bg-background
                      hover:bg-gray-100 transition-colors ease-in-out hover:text-foreground'
                  href={link.href}
                  onClick={() => {
                    link.onClick?.();
                    close();
                  }}
                >
                  <link.icon className='size-4' aria-hidden='true' />
                  {link.label}
                </Link>
              ))}

              <Separator />

              <button
                className='whitespace-nowrap flex items-center gap-x-2 px-4 py-2.5 w-full border-b border-gray-200
                    bg-background hover:bg-gray-100 transition-colors ease-in-out rounded-b-lg hover:text-foreground'
                onClick={() => {
                  onSignOut?.();

                  // Reset stores
                  setCart(undefined);

                  // Trigger clerk sign-out
                  signOut({ redirectUrl: Routes.root });

                  close();
                }}
              >
                <DoorOpen className='size-4' aria-hidden='true' />
                Sign Out
              </button>

              <div className='flex-center h-12'>
                <FullLightLogo width={124} height={56} />
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

function ProfileButtonSkeleton({ isDark }: { isDark: boolean }) {
  return (
    <div className='flex items-center gap-x-2.5 w-fit'>
      <Skeleton
        className={clsx('rounded-full size-8', isDark && 'opacity-25')}
      />
      <Skeleton
        className={clsx('w-[72px] h-6 hidden sm:block', isDark && 'opacity-25')}
      />
    </div>
  );
}
